import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AxiosController from "../../../authentication/AxiosController";
import Loader from "../../../loader/Loader";
import { notifyError, notifySuccess } from "../../../toastify/toastify";

/**
 * ShopifyCallback - Handles the Shopify OAuth redirect.
 *
 * Flow:
 * 1. User clicks "Connect Shopify" → backend returns Shopify auth URL
 * 2. User is redirected to Shopify to authorize
 * 3. Shopify redirects back to THIS frontend route with ?code=...&shop=...&state=...
 * 4. This component captures those params and POSTs them to the backend
 *    (with the user's Bearer token so the backend knows which user to associate)
 * 5. On success, navigates to the Shopify integration page
 */
const ShopifyCallback = () => {
  const { slug, lang } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [_] = useTranslation("global");
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get("code");
    const shop = urlParams.get("shop");
    const state = urlParams.get("state");

    if (!code || !shop) {
      setError("Missing OAuth parameters from Shopify");
      setLoading(false);
      return;
    }

    if (!token) {
      // User is not logged in, redirect to login
      navigate(`/${slug}/${lang}/auth/login`);
      return;
    }

    // Send the OAuth code to the backend with Bearer token
    AxiosController.post(
      `api/${slug}/${lang}/shopify/oauth/callback`,
      { code, shop, state },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    )
      .then((res) => {
        notifySuccess(_("panel.shopifyConnected"));
        // Navigate to the Shopify integration page with success indicator
        navigate(
          `/${slug}/${lang}/panel/outside-integrations/shopify?shopify_connected=true`,
          { replace: true }
        );
      })
      .catch((e) => {
        const message =
          e?.response?.data?.message || "Failed to connect Shopify store";
        setError(message);
        notifyError(message);
        setLoading(false);
      });
  }, []);

  if (error) {
    return (
      <div className="d-flex flex-column align-items-center gap-3 py-5">
        <div
          className="alert alert-danger text-center"
          style={{ maxWidth: "500px" }}
        >
          <h5>{_("panel.shopifySendError")}</h5>
          <p>{error}</p>
        </div>
        <button
          className="btn"
          style={{
            backgroundColor: "rgb(89, 198, 201)",
            color: "white",
          }}
          onClick={() =>
            navigate(
              `/${slug}/${lang}/panel/outside-integrations/shopify`
            )
          }
        >
          {_("panel.shopify")}
        </button>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column align-items-center gap-3 py-5">
      <Loader active={loading} />
      <p className="fs-5">{_("panel.shopifyConnecting")}</p>
    </div>
  );
};

export default ShopifyCallback;
